import React, { useEffect, useRef } from 'react';
import { ChatMessage } from './chat-message';
import type { ChatMessage as ChatMessageType } from './hooks/use-league-chat';
import { Loader2, MessageSquare } from 'lucide-react';
import { isToday, isYesterday, isSameDay, format } from 'date-fns';

function formatDayLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  const now = new Date();
  if (date.getFullYear() !== now.getFullYear()) {
    return format(date, 'MMM d, yyyy');
  }
  return format(date, 'MMM d');
}

interface ChatMessageListProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  error: string | null;
  currentUserId: string | null;
  isOwner: boolean;
  onReport: (message: ChatMessageType) => void;
  onDelete: (messageId: string) => void;
  onMuteUser?: (userId: string, username: string) => void;
  onPersonalMuteUser?: (userId: string, username: string) => void;
  personalMutedUserIds?: string[];
  scrollTrigger?: number; // Changes to trigger scroll to bottom
}

export function ChatMessageList({
  messages,
  isLoading,
  error,
  currentUserId,
  isOwner,
  onReport,
  onDelete,
  onMuteUser,
  onPersonalMuteUser,
  personalMutedUserIds = [],
  scrollTrigger,
}: ChatMessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom whenever messages change (including initial load and new messages)
  // Multiple scroll attempts to handle image loading
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      };

      // Immediate scroll after DOM paint
      requestAnimationFrame(() => {
        scrollToBottom();
      });

      // Additional scrolls to handle lazy-loaded images and content
      const timers = [
        setTimeout(scrollToBottom, 100),
        setTimeout(scrollToBottom, 300),
        setTimeout(scrollToBottom, 500),
      ];

      return () => timers.forEach(clearTimeout);
    }
  }, [isLoading, messages.length, scrollTrigger]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-sm">Loading messages...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-destructive">
          <p className="font-medium">Error loading chat</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No messages yet</p>
          <p className="text-sm">Be the first to start the conversation!</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto px-4 py-3"
    >
      {messages.map((message, index) => {
        const prevMessage = index > 0 ? messages[index - 1] : null;
        const showDaySeparator = !prevMessage || !isSameDay(message.createdAt, prevMessage.createdAt);

        return (
          <React.Fragment key={message.id}>
            {showDaySeparator && (
              <div className="flex items-center justify-center my-4">
                <div className="flex-1 border-t border-border" />
                <span className="text-xs text-muted-foreground px-3 whitespace-nowrap">
                  {formatDayLabel(message.createdAt)}
                </span>
                <div className="flex-1 border-t border-border" />
              </div>
            )}
            <ChatMessage
              message={message}
              isOwnMessage={message.userId === currentUserId}
              isOwner={isOwner}
              currentUserId={currentUserId}
              onReport={onReport}
              onDelete={onDelete}
              onMuteUser={onMuteUser}
              onPersonalMuteUser={onPersonalMuteUser}
            />
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}
